import React, { type CSSProperties, type ReactNode, useEffect, useImperativeHandle, useRef } from "react";
import Swipe, { type SwipeOptions } from "swipe-js-iso";

interface Props {
  swipeOptions?: SwipeOptions;
  style?: {
    container?: CSSProperties;
    wrapper?: CSSProperties;
    child?: CSSProperties;
  };
  className?: string;
  childCount?: number;
  id?: string;
  children: (childStyles: CSSProperties) => ReactNode;
}

export interface ReactSwipeRef {
  prev(): void;
  next(): void;
  getPos(): number;
  getNumSlides(): number;
  slide(index: number, duration: number): void;
}

export const ReactSwipe = React.forwardRef<ReactSwipeRef, Props>(
  (
    {
      swipeOptions = {},
      style = {
        container: {
          overflow: "hidden",
          visibility: "hidden",
          position: "relative",
        },
        wrapper: {
          overflow: "hidden",
          position: "relative",
          display: "flex",
        },
        child: {
          width: "100%",
          position: "relative",
          transitionProperty: "transform",
        },
      },
      childCount,
      id,
      className = "",
      children,
    },
    ref,
  ) => {
    const swipeInstance = useRef<Swipe | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // const prevSwipeOptions = useRef(swipeOptions);

    useImperativeHandle(ref, () => ({
      getNumSlides(): number {
        return swipeInstance.current?.getNumSlides() ?? 0;
      },
      prev() {
        swipeInstance.current?.prev();
      },
      slide(index: number, duration: number) {
        swipeInstance.current?.slide(index, duration);
      },
      next() {
        swipeInstance.current?.next();
      },
      getPos() {
        return swipeInstance.current?.getPos() ?? 0;
      },
    }));

    useEffect(() => {
      let unmounted = false;
      const setupSwipe = () => {
        if (unmounted) {
          return;
        }
        swipeInstance.current?.kill();
        if (containerRef.current) {
          const { width } = containerRef.current.getBoundingClientRect();
          if (width === 0) {
            requestAnimationFrame(setupSwipe);
            return;
          }
          swipeInstance.current = new Swipe(containerRef.current, swipeOptions);
        } else {
          swipeInstance.current = null;
        }
      };
      setupSwipe();
      return () => {
        unmounted = true;
        swipeInstance.current?.kill();
        swipeInstance.current = null;
      };
    }, [childCount]);

    return (
      <div id={id} ref={containerRef} className={`react-swipe-container ${className}`} style={style.container}>
        <div style={style.wrapper}>{children(style.child!)}</div>
      </div>
    );
  },
);
